import PropTypes from 'prop-types'

import Placeholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Placeholder'
import { getQuestionCount } from '@aytm/app/stats/lib/stat-page'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// styles
import style from '@aytm/app/stats/page/question-views/components/charts/placeholders/styles/Chart.module.scss'
import classNames from 'classnames'

// A pulsing stand-in costs one animation per block, so a long report leaves them still.
const ANIMATED_REPORT_LIMIT = 50

// The height a stats chart is drawn at; a chart with a height of its own passes it instead.
export const CHART_PLACEHOLDER_HEIGHT = CHART_HEIGHT

const Chart = ({ children, height: ownHeight, required = false, isLoading, isInverse, isAnimated, onLoad }) => {
  // Derived here rather than handed down, so a block that passes nothing still animates; an
  // explicit value still wins, which is how an export forces it off whatever the report's size.
  // A count of zero is a page with no report registered, not a short one.
  const questionCount = getQuestionCount()
  // null too: a mode that draws no chart reports its height as null
  const height = ownHeight ?? CHART_PLACEHOLDER_HEIGHT
  const animated = isAnimated ?? (questionCount > 0 && questionCount < ANIMATED_REPORT_LIMIT)

  const renderStandIn = ref => {
    const count = Math.max(0, Math.floor((height - 100) / 55))
    return (
      <div
        ref={ref}
        className={classNames(style.placeholder, {
          [style['placeholder-animated']]: animated,
          [style['placeholder-inverse']]: isInverse
        })}
        style={{ height: `${height}px` }}
      >
        <div className={style['placeholder-dots']}>
          {Array.from(Array(3)).map((_, index) => (
            <span key={index + 1}></span>
          ))}
        </div>
        <div className={style['placeholder-header']}>
          <div className={style['placeholder-legend']}>
            {Array.from(Array(3)).map((_, index) => (
              <div key={index + 1}>
                <span></span>
              </div>
            ))}
          </div>
        </div>
        <div className={style['placeholder-body']}>
          <div className={style['placeholder-body-yaxis']}>
            {Array.from(Array(count + 1)).map((_, index) => (
              <span key={index + 1}></span>
            ))}
          </div>
          <div className={style['placeholder-body-xaxis']}>
            {Array.from(Array(count)).map((_, index) => (
              <span key={index + 1}></span>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <Placeholder
      height={height}
      required={required}
      loadAll={window.$FullChartsLoading || false}
      isLoading={isLoading}
      onLoad={onLoad}
      renderStandIn={renderStandIn}
    >
      {children}
    </Placeholder>
  )
}

Chart.displayName = 'Chart'

Chart.propTypes = {
  children: PropTypes.any,
  height: PropTypes.number,
  required: PropTypes.bool,
  isLoading: PropTypes.bool,
  isInverse: PropTypes.bool,
  isAnimated: PropTypes.bool,
  onLoad: PropTypes.func
}

export default Chart
